/**
 * Phase 28 — Live PCM audio streaming player.
 *
 * Connects to a Bland.ai live-listen WebSocket and plays the raw PCM Int16
 * mono @ 16 kHz, little-endian stream in the browser via Web Audio API.
 *
 * Bland's listen WS sends binary frames continuously while the call is active.
 * We decode each frame into Float32, schedule it back-to-back on the audio
 * context's timeline, and clean up automatically when the WS closes.
 *
 * Usage:
 *   const player = new LiveListenPlayer(wsUrl);
 *   await player.start();
 *   // ...later:
 *   player.stop();
 */
export class LiveListenPlayer {
  constructor(wsUrl, { onStateChange, onError } = {}) {
    this.wsUrl = wsUrl;
    this.onStateChange = onStateChange || (() => {});
    this.onError = onError || (() => {});
    this.ws = null;
    this.audioCtx = null;
    this.nextStartTime = 0;
    this.state = "idle"; // idle | connecting | playing | ended | error
    this.bytesReceived = 0;
  }

  setState(s) {
    this.state = s;
    this.onStateChange(s);
  }

  async start() {
    if (this.state === "playing" || this.state === "connecting") return;
    this.setState("connecting");
    try {
      // Lazy-create AudioContext on user gesture (most browsers require this)
      const Ctx = window.AudioContext || window.webkitAudioContext;
      this.audioCtx = new Ctx({ sampleRate: 16000 });
      // Some browsers (Safari) start the ctx suspended; resume on user gesture
      if (this.audioCtx.state === "suspended") {
        try { await this.audioCtx.resume(); } catch (_) { /* ignore */ }
      }
      this.nextStartTime = this.audioCtx.currentTime + 0.1; // 100ms prebuffer

      this.ws = new WebSocket(this.wsUrl);
      this.ws.binaryType = "arraybuffer";

      this.ws.onopen = () => {
        this.setState("playing");
      };
      this.ws.onmessage = (evt) => this._handleFrame(evt.data);
      this.ws.onerror = (e) => {
        this.onError(e);
        this.setState("error");
      };
      this.ws.onclose = () => {
        this.setState("ended");
      };
    } catch (e) {
      this.onError(e);
      this.setState("error");
    }
  }

  _handleFrame(data) {
    if (!(data instanceof ArrayBuffer) || !this.audioCtx) return;
    this.bytesReceived += data.byteLength;
    // Bland: PCM Int16 mono 16kHz little-endian → convert to Float32 [-1, 1]
    const pcm = new Int16Array(data);
    const floats = new Float32Array(pcm.length);
    for (let i = 0; i < pcm.length; i++) {
      floats[i] = pcm[i] / 32768;
    }
    // Wrap in an AudioBuffer and schedule
    const buffer = this.audioCtx.createBuffer(1, floats.length, 16000);
    buffer.getChannelData(0).set(floats);

    const src = this.audioCtx.createBufferSource();
    src.buffer = buffer;
    src.connect(this.audioCtx.destination);

    const now = this.audioCtx.currentTime;
    // If we fell behind (browser tab went background, etc.), resync to "now+50ms"
    if (this.nextStartTime < now) {
      this.nextStartTime = now + 0.05;
    }
    src.start(this.nextStartTime);
    this.nextStartTime += buffer.duration;
  }

  stop() {
    try { if (this.ws) this.ws.close(); } catch (_) { /* ignore */ }
    try { if (this.audioCtx) this.audioCtx.close(); } catch (_) { /* ignore */ }
    this.ws = null;
    this.audioCtx = null;
    this.setState("ended");
  }
}
